/**
 * Pure helpers for template placeholders.
 *
 * A placeholder lives in the document as one or more Writer bookmarks named
 * `zph_<key>__<nn>` (key = slug, nn = 2-digit placement index). These
 * helpers build and parse those names, group what the editor enumerates,
 * derive keys from labels, and check values the same way the backend does
 * (`app/modules/nodes/schemas/placeholders.py::validate_value`) so the panel
 * can refuse before a round-trip.
 */

export const PREFIX = 'zph_';
export const KEY_RE = /^[a-z][a-z0-9_]{0,23}$/;
export const NAME_RE = /^zph_([a-z][a-z0-9_]{0,23})__(\d{2})$/;
export const TYPES = ['text', 'multiline', 'number', 'date', 'choice'];
export const MAX_KEY_LEN = 24;
export const MAX_VALUE_LEN = 4000;
export const MAX_PLACEMENTS = 99;
// The backend's PlaceholderDefinition limits.
export const MAX_LABEL_LEN = 120;
export const MAX_PLACEHOLDERS = 100;
export const MAX_OPTIONS = 50;
export const MAX_OPTION_LEN = 200;
// A number as a person types it — the backend's NUMBER_RE: optional sign,
// digits, one decimal point or comma, optional exponent. No thousands
// separators.
export const NUMBER_RE = /^[+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+)(?:[eE][+-]?\d+)?$/;
// A date placeholder's year range (MIN_DATE_YEAR / MAX_DATE_YEAR).
export const MIN_DATE_YEAR = 1800;
export const MAX_DATE_YEAR = 2200;

/** A real calendar day as YYYY-MM-DD within the year range (no Feb 30). */
export function isPlaceholderDate(text) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!m) return false;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (year < MIN_DATE_YEAR || year > MAX_DATE_YEAR) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

export function bookmarkName(key, index) {
  return `${PREFIX}${key}__${String(index).padStart(2, '0')}`;
}

export function parseBookmarkName(name) {
  const m = NAME_RE.exec(name || '');
  return m ? { key: m[1], index: Number(m[2]) } : null;
}

/** {key: [names in the order the editor listed them]} — malformed names skipped. */
export function groupPlacements(names) {
  const groups = {};
  for (const name of names || []) {
    const parsed = parseBookmarkName(name);
    if (!parsed) continue;
    (groups[parsed.key] ||= []).push(name);
  }
  return groups;
}

/** The next free placement index for `key`, or null when 99 are placed. */
export function nextIndex(names, key) {
  let max = 0;
  for (const name of names || []) {
    const parsed = parseBookmarkName(name);
    if (parsed && parsed.key === key && parsed.index > max) max = parsed.index;
  }
  return max >= MAX_PLACEMENTS ? null : max + 1;
}

// Cyrillic (ru + the Uzbek letters) → Latin, so a label typed in the UI
// language still yields a usable key.
const CYRILLIC = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh', з: 'z', и: 'i', й: 'y',
  к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f',
  х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  ў: 'o', қ: 'q', ғ: 'g', ҳ: 'h',
};

/** A key candidate from a label: lowercase ASCII slug, letter-first, ≤24 chars. */
export function slugify(label) {
  let s = String(label || '').toLowerCase();
  s = s.replace(/[а-яёўқғҳ]/g, (ch) => CYRILLIC[ch] ?? '');
  s = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
  s = s.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/_+/g, '_');
  if (s && !/^[a-z]/.test(s)) s = `f_${s}`;
  s = s.slice(0, MAX_KEY_LEN).replace(/_+$/, '');
  return s || 'field';
}

/** `key`, or `key_2`, `key_3`… when it is already taken. */
export function uniqueKey(key, taken) {
  const used = new Set(taken || []);
  if (!used.has(key)) return key;
  for (let n = 2; n < 1000; n += 1) {
    const suffix = `_${n}`;
    const candidate = `${key.slice(0, MAX_KEY_LEN - suffix.length)}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  return key;
}

/**
 * Error keyword for a value that does not fit its definition, else null.
 * `strict` also enforces `required` (what Apply does; drafts don't).
 * Keywords match the i18n `placeholders.errors.*` keys.
 */
export function validateValue(def, value, { strict = false } = {}) {
  const text = value == null ? '' : String(value);
  if (!text.trim()) return strict && def.required ? 'required' : null;
  if (text.length > MAX_VALUE_LEN) return 'tooLong';
  switch (def.type) {
    case 'text':
      return /[\r\n]/.test(text) ? 'multiline' : null;
    case 'number':
      return NUMBER_RE.test(text.trim()) ? null : 'number';
    case 'date':
      return isPlaceholderDate(text) ? null : 'date';
    case 'choice':
      return (def.options || []).includes(text) ? null : 'choice';
    default:
      return null;
  }
}

/** Definition-level checks mirrored from the backend schema; error keyword or null. */
export function validateDefinition(def, takenKeys = []) {
  if (!def.label || !String(def.label).trim()) return 'labelRequired';
  if (String(def.label).trim().length > MAX_LABEL_LEN) return 'labelTooLong';
  if (!KEY_RE.test(def.key || '')) return 'keyInvalid';
  if (takenKeys.includes(def.key)) return 'keyDuplicate';
  if (takenKeys.length >= MAX_PLACEHOLDERS) return 'tooManyPlaceholders';
  if (!TYPES.includes(def.type)) return 'typeInvalid';
  const options = (def.options || []).map((o) => String(o).trim()).filter(Boolean);
  if (def.type === 'choice' && options.length === 0) return 'optionsRequired';
  if (def.type !== 'choice' && options.length > 0) return 'optionsOnlyChoice';
  if (options.length > MAX_OPTIONS) return 'optionsTooMany';
  if (options.some((o) => o.length > MAX_OPTION_LEN)) return 'optionTooLong';
  if (new Set(options).size !== options.length) return 'optionsDuplicate';
  if (def.defaultValue && validateValue({ ...def, options }, def.defaultValue)) return 'defaultInvalid';
  return null;
}

/** Options as the user types them (one per line) → clean list. */
export function parseOptions(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((o) => o.trim())
    .filter(Boolean);
}

// ---------------------------------------------------------------- extents

// A placement's EXTENT is what its bookmark covers in the saved bytes
// (GET /file/{id}/placeholders/placements). Bookmarks follow edits and have
// no visible boundary: paste over the `[Label]` marker and the mark wraps
// the whole paste, so the next fill replaces that text too. The warning is
// on SIZE, never on "text ≠ [Label]" — labels get renamed after insertion.
export const EXTENT_MAX_CHARS = 80;
export const BLANK_SENTINEL = '\u00a0';

/** 'long' | 'multiline' | null — why an extent deserves a warning. */
export function extentIssue(placement, { maxChars = EXTENT_MAX_CHARS } = {}) {
  if (!placement) return null;
  if ((placement.breaks || 0) > 0) return 'multiline';
  if ((placement.chars || 0) > maxChars) return 'long';
  return null;
}

/** Display form of an extent: blank sentinel → '', whitespace collapsed, ellipsized. */
export function describeExtent(text, max = 60) {
  const s = String(text ?? '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1).trimEnd()}…`;
}

/** {key: [placements sorted by index]} from the endpoint's rows. */
export function groupExtents(items) {
  const groups = {};
  for (const item of items || []) {
    if (!item || !item.key) continue;
    (groups[item.key] ||= []).push(item);
  }
  for (const list of Object.values(groups)) list.sort((a, b) => a.index - b.index);
  return groups;
}
